# Polling

Event polling has two primary uses:

1. Paging through historical events.

   If you need to backfill a webhook subscription or reprocess missed events,
   we strongly recommend using our event [replay] functionality instead.
   This reduces the complexity of your integration;
   the SEEK API will take care of requeuing each event in scope,
   and your webhook endpoint will process a replayed event as it would any other event.

   If you want to interactively explore historical events you can use the [Developer Dashboard events page].

2. Event delivery for partners without the capability to consume webhooks.

[replay]: /events/webhooks#replaying-a-subscriptions-events

Both flows use the [`events` query] for [cursor-based pagination] through events.

The `events` query supports filtering on [scheme ID], event types and an optional date range.
You can implement more sophisticated event categorisation or filtering within your polling software.
For example, you might select the [`CandidateProfile.associatedPositionProfile`] from a [`CandidateApplicationCreated` event] to identify the associated position and hirer.

To avoid wasted resources from repeatedly polling each hirer, filtering events by hirer ID is intentionally unsupported.
Use [webhooks] to deliver events and perform filtering within your webhook endpoint.

[Developer Dashboard events page]: /manage/events

## Backfilling & reprocessing flow

If you use [webhook] subscriptions,
we strongly recommend using our event [replay] functionality instead.

This section remains as a reference for integrations that use polling-based event delivery.

```mermaid Backfilling interaction
sequenceDiagram
  participant P as Partner
  participant A as auth.seek.com
  participant G as graphql.seek.com
  P->>A: Send client credentials
  A-->>P: Issue partner token
  P->>G: Use `events` query with `after: null`
  G-->>P: Return first page of events and `endCursor`
  loop While `hasNextPage`
    P->>G: Use `events` query with the previous `endCursor`
    G-->>P: Return events and a new `endCursor`
  end
```

This flow is based on paging through events within a specific time range:

- For processing missed or failed events you can specify the `afterDateTime` and `beforeDateTime` of the affected period.
- For backfilling events for a webhook subscription you can specify just the `beforeDateTime` of when the subscription was created.

You then repeatedly use the [`events` query](/schema/query/events) with the determined `afterDateTime` and `beforeDateTime` arguments.
The initial query should pass `after: null` to get the first page.

For example, this queries the first page of 25 `CandidateApplicationCreated` events between April 10th and 20th 2020:

```graphql
query (
  $after: String
  $filter: EventsFilterInput!
  $first: Int!
  $schemeId: String!
) {
  events(after: $after, filter: $filter, first: $first, schemeId: $schemeId) {
    edges {
      node {
        typeCode
        ... on CandidateApplicationCreatedEvent {
          candidate {
            person {
              name {
                formattedName
              }
            }
          }
          candidateApplicationProfile {
            associatedPositionProfile {
              positionUri
            }
          }
        }
      }
    }
    pageInfo {
      hasNextPage
      endCursor
    }
  }
}
```

```json
{
  "after": null,
  "filter": {
    "eventTypeCodes": "CandidateApplicationCreated",
    "afterDateTime": "2020-04-10T00:00:00Z",
    "beforeDateTime": "2020-04-21T00:00:00Z"
  },
  "first": 25,
  "schemeId": "seekAnzPublicTest"
}
```

For subsequent queries you pass the previous page’s `endCursor` as the `after` argument.
Once `hasNextPage` is `false` there are no more events in the time range and the process is complete.

Events will remain for 90 days after they occur.
Your software must store any data that it needs to access after the 90 day period.

## Event delivery flow

```mermaid Event delivery interaction
sequenceDiagram
  participant P as Partner
  participant A as auth.seek.com
  participant G as graphql.seek.com
  loop Every 15 minutes
    P->>A: Send client credentials
    A-->>P: Issue partner token
    P->>G: Use the `events` query with the previous `endCursor`
    G-->>P: Return events and `endCursor`
  end
```

In this flow the [`events` query] is used with the previous poll’s `endCursor` as its `after` argument.
The cursor is persisted by your software to resume event polling from the last processed event.
If it’s difficult to persist the cursor you can instead use [webhooks] to receive events statelessly.

For accurate paging only the `after` argument should be used;
you should not specify an `afterDateTime` or `beforeDateTime`.
This ensures that polling will resume from the last successful poll in the event of a failure.

For example, this queries for a page of 25 `CandidateApplicationCreated` events since the previous cursor:

```graphql
query (
  $after: String!
  $filter: EventsFilterInput!
  $first: Int!
  $schemeId: String!
) {
  events(after: $after, filter: $filter, first: $first, schemeId: $schemeId) {
    edges {
      node {
        typeCode
        ... on CandidateApplicationCreatedEvent {
          candidate {
            person {
              name {
                formattedName
              }
            }
          }
          candidateApplicationProfile {
            associatedPositionProfile {
              positionUri
            }
          }
        }
      }
    }
    pageInfo {
      hasNextPage
      endCursor
    }
  }
}
```

```json
{
  "after": "seekAnzPublicTest:eventCursor:events:3zQw2pr4e9HHhsv8CJUhxMJPMA7mM4L13",
  "filter": {
    "eventTypeCodes": "CandidateApplicationCreated"
  },
  "first": 25,
  "schemeId": "seekAnzPublicTest"
}
```

To process all new events the `events` query should be repeated until `hasNextPage` is `false`.

## Limits

### Polling interval

Once the last page of events has been reached you should not poll again for at least 15 minutes.
If you’re polling multiple event streams you must not exceed one `events` query per minute across your entire SEEK API integration.
This is a global limit applied across all of your servers, hirers and webhook subscriptions.

If near real-time event delivery is required then [webhooks] can be used instead.
See [“why webhooks”] for an explanation of why frequent polling is harmful.

### Subscription streams

The [`events` query] can be used in tandem with [`filter.subscriptionId`] to obtain an ad-hoc historical view of the events associated with a particular subscription.
This can help you to discover that historical events are missing or unprocessed,
which you can subsequently remediate by triggering a [replay].
We recommend the [Developer Dashboard webhooks page] for ad-hoc troubleshooting of this nature.

Polling implementations are approved on a case-by-case basis and are mandated to operate at a partner level.
Your production environment must not implement event delivery by polling subscription-scoped event streams.
This limitation ensures that the SEEK API can maintain its quality of service and operating costs as partner usage and webhook subscriptions scale over time.

[“why webhooks”]: /events#why-webhooks
[`candidateapplicationcreated` event]: /schema/named-type/CandidateApplicationCreatedEvent
[`candidateprofile.associatedpositionprofile`]: /schema/named-type/CandidateProfile#/field/associatedPositionProfile
[`events` query]: /schema/query/events
[`filter.subscriptionid`]: /schema/named-type/EventsFilterInput#/field/subscriptionId
[cursor-based pagination]: /graphql/seek-api-conventions#cursor-based-pagination
[Developer Dashboard webhooks page]: /manage/webhooks
[scheme id]: /graphql/seek-api-conventions#scheme-identifiers
[webhook]: /events/webhooks
[webhooks]: /events/webhooks
